import { FC, useCallback, useContext } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { NotificationContext } from '../../../providers'
import {
  useCreateCannedJobMotorFluidsMutation,
  useUpdateCannedJobMotorFluidsMutation
} from '../../../redux/api/cannedJobApi'
import { useGetFluidsTaxonomyQuery } from '../../../redux/api/taxonomyApi'
import {
  CannedJobMotorFluidsEntry,
  CannedJobMotorFluidsInfo,
  CannedJobMotorFluidsInfoUnitOfMeasureEnum,
  CannedJobMotorLaborEntry,
  MotorTaxonomy
} from '../../../sdk'
import { NONE_ID } from '../../../utils/convertEmptyStringToUndefined/convertEmptyStringToUndefined'
import {
  generateNodeIdFromTaxonomy,
  getTaxonomyFromMotorTaxonomyTree
} from '../../../utils/convertTaxonomy/convertTaxonomy'
import { SearchFluidsTaxonomyWithoutVehicle } from './SearchFluidsTaxonomyWithoutVehicle'

export const CONTENT_HEIGHT = '70vh'
export interface CannedJobMotorFluidsFormInput {
  name?: string | undefined
  partsMatrix?: string | undefined
  taxExempt?: boolean | undefined
  suggestedPricePerUnit?: number | undefined
  unitOfMeasure?: CannedJobMotorFluidsInfoUnitOfMeasureEnum | undefined
}
const convertCannedJobMotorFluidsEntry: (
  defaultValue: CannedJobMotorFluidsEntry | undefined
) => CannedJobMotorFluidsFormInput = (defaultValue) => {
  if (!defaultValue) {
    return {
      name: '',
      partsMatrix: undefined,
      taxExempt: false
    }
  }
  return {
    ...defaultValue.info!,
    partsMatrix:
      defaultValue.info!.suggestedPricePerUnit !== undefined
        ? NONE_ID
        : defaultValue.info!.partsMatrix
  }
}

const convertToCannedJobMotorFluidsInfo: (
  data: CannedJobMotorFluidsFormInput,
  motorTaxonomy: MotorTaxonomy,
  selectedTree: string,
  cannedJobId: string
) => CannedJobMotorFluidsInfo = (
  data,
  motorTaxonomy,
  selectedTree,
  cannedJobId
) => {
  const taxonomy = getTaxonomyFromMotorTaxonomyTree(motorTaxonomy, selectedTree)
  const partsMatrix = data.partsMatrix
  const suggestedPricePerUnit = data.partsMatrix
    ? undefined
    : (data.suggestedPricePerUnit! as number)
  const unitOfMeasure =
    data.unitOfMeasure &&
    (data.unitOfMeasure! as CannedJobMotorFluidsInfoUnitOfMeasureEnum)
  return {
    cannedJob: cannedJobId,
    ...data,
    taxonomy,
    partsMatrix,
    suggestedPricePerUnit,
    unitOfMeasure
  }
}

interface Props {
  cancelCallback: () => void
  submitCallback: () => void
  cannedJobId: string
  existingEntry: CannedJobMotorLaborEntry | undefined
}
export const LookupMotorFluidsWindow: FC<Props> = ({
  cancelCallback,
  submitCallback,
  cannedJobId,
  existingEntry
}) => {
  const { accessToken } = useAccessTokenAndShopId()
  const { data: motorTaxonomy } = useGetFluidsTaxonomyQuery({
    authorization: accessToken
  })
  const [createCannedJobMotorFluids] = useCreateCannedJobMotorFluidsMutation()
  const [updateCannedJobMotorFluids] = useUpdateCannedJobMotorFluidsMutation()

  const { setError } = useContext(NotificationContext)
  const handleSubmit = useCallback(
    async (data: CannedJobMotorFluidsFormInput, selectedTree: string) => {
      if (!motorTaxonomy || !selectedTree) {
        setError('Please select a value from the taxonomy tree')
        return
      }
      const dataToSend = convertToCannedJobMotorFluidsInfo(
        data,
        motorTaxonomy,
        selectedTree,
        cannedJobId
      )
      if (existingEntry) {
        updateCannedJobMotorFluids({
          authorization: accessToken,
          cannedJobMotorFluidsEntry: {
            id: existingEntry.id!,
            info: dataToSend
          }
        })
      } else {
        createCannedJobMotorFluids({
          authorization: accessToken,
          cannedJobMotorFluidsInfo: dataToSend
        })
      }
      submitCallback()
    },
    [
      motorTaxonomy,
      existingEntry,
      submitCallback,
      updateCannedJobMotorFluids,
      accessToken,
      cannedJobId,
      createCannedJobMotorFluids,
      setError
    ]
  )

  return (
    <SearchFluidsTaxonomyWithoutVehicle
      cancelCallback={cancelCallback}
      onSubmit={handleSubmit}
      motorTaxonomy={motorTaxonomy}
      defaultInput={convertCannedJobMotorFluidsEntry(existingEntry)}
      defaultSelectedTree={generateNodeIdFromTaxonomy(
        existingEntry?.info!.taxonomy
      )}
    />
  )
}
